import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse";

import {
  Container,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Alert,
  Button,
  Input,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import {
  BUTTON_COLORS,
  SIZES,
  STATUS,
  TYPOGRAPHY_VARIANT,
  VARIANTS,
} from "../../../common/constants/commonConstants";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
import { getCampaigns } from "../../../services/campaign";
import {
  CAMPAIGN_ERRORS,
  CAMPAIGN_MESSAGE,
} from "../../../common/constants/campaignModule/campaignConstant";
import { uploadCsvFile } from "../../../services/dataModule/dataUpload";
import {
  DATA_UPLOAD_ERRORS,
  DATA_UPLOAD_STRINGS,
  DATA_UPLOAD_SUCCESS,
} from "../../../common/constants/dataModule/dataUploadContstant";
import { Download } from "@mui/icons-material";

// ✅ Only validate identity fields on the frontend.
// Numeric and Yes/No fields are sanitized/defaulted on the backend.
const requiredFields = [
  "customeruniqueid",
  "firstname",
  "lastname",
  "customerid",
];

// Campaign filter option sets (mirror the campaign list pages)
const CATEGORY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Other",
];
const LANGUAGE_OPTIONS = ["English", "Spanish"];
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const todayCategory = WEEKDAYS[new Date().getDay()]; // default Category filter = current day

// Conversation action options.
// scope wording is deliberate:
//   mode 1 -> campaign-scoped reset (udp_updateschedule_CampaignConv), hits
//             EVERY customerdata row for the campaign, including users absent
//             from the file.
//   mode 2 -> customer-scoped reset (udp_endoldconversations), crosses campaign
//             boundaries but only touches users present in the file.
//   mode 3 -> same scope as 2, without the refresh/restart.
const CONVERSATION_MODES = [
  {
    value: "1",
    title: "Reset this campaign",
    detail:
      "Ends all conversations in the selected campaign, including for users not in the file. Users in the file get refreshed data and new conversations by running the campaign schedules. Other campaigns are unaffected.",
  },
  {
    value: "2",
    title: "Reset these users everywhere",
    detail:
      "Ends conversations in every campaign for users in the file, then refreshes their data and starts new conversations. Users not in the file are untouched.",
  },
  {
    value: "3",
    title: "End these users' conversations",
    detail:
      "Ends conversations in every campaign for users in the file. No data refresh, no new conversations. Users not in the file are untouched.",
  },
];

const DataUpload = () => {
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);
  const [data, setData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(todayCategory);
  const [languageFilter, setLanguageFilter] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [conversationMode, setConversationMode] = useState("1");
  const userId = localStorage.getItem("userId") || "";
  const theme = useTheme();

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      try {
        const response = await getCampaigns();
        setCampaigns(response);
      } catch (error) {
        console.error(CAMPAIGN_ERRORS.FETCH_CAMPAIGN_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  // Campaigns visible after applying the Category + Language filters
  const filteredCampaigns = (campaigns || []).filter((c) => {
    const matchesCategory =
      categoryFilter === "All" || c.Category === categoryFilter;
    const matchesLanguage =
      languageFilter === "All" || c.Language === languageFilter;
    return matchesCategory && matchesLanguage;
  });

  // If the selected campaign falls outside the current filter, clear it
  // so the Autocomplete value never points to a hidden option.
  useEffect(() => {
    if (!selectedCampaign) return;
    const stillVisible = (campaigns || []).some(
      (c) =>
        c.id === selectedCampaign &&
        (categoryFilter === "All" || c.Category === categoryFilter) &&
        (languageFilter === "All" || c.Language === languageFilter)
    );
    if (!stillVisible) setSelectedCampaign("");
  }, [categoryFilter, languageFilter, campaigns, selectedCampaign]);

  // ✅ Validate only required identity fields
  const validateRow = (row, index) => {
    const errs = [];
    requiredFields.forEach((field) => {
      const val = row[field];
      if (val === undefined || val === null || String(val).trim() === "") {
        errs.push(`${field} is required in row ${index + 1}`);
      }
    });
    return errs;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0] || null;

    setErrors([]);
    setData([]);
    setCsvFile(null);

    if (!file) return;

    try {
      setIsLoading(true);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().toLowerCase(),
        complete: (results) => {
          const validationErrors = [];

          // ✅ Validate required identity fields only
          results.data.forEach((row, index) => {
            validationErrors.push(...validateRow(row, index));
          });

          setErrors(validationErrors);

          if (validationErrors.length === 0) {
            setData(results.data);
            // ✅ Upload original file as-is. Backend handles sanitization
            // (numeric defaults, Y/N normalization, NULL/N/A handling, etc.)
            setCsvFile(file);
          }
        },
        error: (err) => {
          console.error("PapaParse error:", err);
          setErrorMessage(DATA_UPLOAD_ERRORS.UPLOAD_CSV_ERROR);
        },
      });
    } catch (error) {
      console.error(DATA_UPLOAD_ERRORS.UPLOAD_CSV_ERROR, error);
      setErrorMessage(DATA_UPLOAD_ERRORS.UPLOAD_CSV_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveToDatabase = async () => {
    if (!selectedCampaign) {
      setValidationError(CAMPAIGN_MESSAGE.SELECT_CAMPAIGN);
      return;
    }
    if (!csvFile) {
      setErrorMessage("Please select a CSV file first.");
      return;
    }
    if (errors.length > 0) {
      setErrorMessage("Please fix CSV validation errors before uploading.");
      return;
    }

    try {
      setIsLoading(true);

      const result = await uploadCsvFile({
        file: csvFile,
        campaignId: selectedCampaign,
        userId: Number(userId),
        conversationMode,
      });

      reset();

      setSuccessMessage(
        result?.message || DATA_UPLOAD_SUCCESS.SAVE_UPLOAD_SUCCESS,
      );
    } catch (error) {
      console.error(DATA_UPLOAD_ERRORS.SAVE_UPLOAD_ERROR, error);
      setErrorMessage(DATA_UPLOAD_ERRORS.SAVE_UPLOAD_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setSelectedCampaign("");
    setData([]);
    setErrors([]);
    setCsvFile(null);
    setValidationError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleDownloadCsv = () => {
    const link = document.createElement("a");
    const publicPath = process.env.PUBLIC_URL || "";
    link.href = `${publicPath}/data/customerData.csv`;
    link.setAttribute("download", "customerData.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container width="xl" disableGutters>
      {successMessage && (
        <Alert severity={STATUS.SUCCESS} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity={STATUS.ERROR} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {isLoading && <ProgressOverlay />}

      {/* Row 1: file input + download on their own row (full file name visible) */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid size={SIZES.GROW}>
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            inputRef={fileInputRef}
            className="mb-4"
            fullWidth
            sx={{
              height: "42px",
              ".MuiInputBase-input": { height: "42px" },
              '& input[type="file"]::file-selector-button': {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                border: "0",
                borderRadius: "4px",
                padding: "0 16px",
                height: "36px",
                marginRight: "16px",
                cursor: "pointer",
              },
            }}
          />
        </Grid>
        <Grid size={SIZES.AUTO}>
          <Button
            variant={VARIANTS.OUTLINED}
            color={BUTTON_COLORS.primary}
            startIcon={<Download />}
            onClick={handleDownloadCsv}
          >
            {DATA_UPLOAD_STRINGS.DOWNLOAD_CSV}
          </Button>
        </Grid>
      </Grid>

      {/* Row 2: Category + Language filters cascade into the Campaign picker */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={SIZES.AUTO}>
          <TextField
            select
            label="Category"
            size={SIZES.SMALL}
            sx={{ minWidth: 150 }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {CATEGORY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={SIZES.AUTO}>
          <TextField
            select
            label="Language"
            size={SIZES.SMALL}
            sx={{ minWidth: 140 }}
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {LANGUAGE_OPTIONS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={SIZES.GROW}>
          <Autocomplete
            disablePortal
            size={SIZES.SMALL}
            options={filteredCampaigns}
            value={
              filteredCampaigns.find((u) => u.id === selectedCampaign) || null
            }
            onChange={(event, newValue) => {
              setSelectedCampaign(newValue?.id || null);
              if (newValue) setValidationError("");
            }}
            getOptionLabel={(option) => (option ? `${option.name}` : "")}
            renderInput={(params) => (
              <TextField {...params} label="Campaigns" />
            )}
          />

          {validationError && (
            <Typography
              variant={VARIANTS.CAPTION}
              gutterBottom
              sx={{ display: "block", mt: 1, color: "red" }}
            >
              {validationError}
            </Typography>
          )}
        </Grid>

        {data && data.length > 0 && (
          <Grid size={SIZES.AUTO}>
            <Typography variant={VARIANTS.CAPTION} sx={{ mt: 1 }}>
              Parsed rows: {data.length}
            </Typography>
          </Grid>
        )}

        {errors.length === 0 && csvFile && selectedCampaign && (
          <Grid size={SIZES.AUTO}>
            <Button
              variant={VARIANTS.CONTAINED}
              color={BUTTON_COLORS.PRIMARY}
              onClick={handleSaveToDatabase}
            >
              {DATA_UPLOAD_STRINGS.SAVE_DATABASE}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Row 3: conversation action */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={SIZES.GROW} sx={{ alignSelf: "flex-start" }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Conversation action</FormLabel>
            <RadioGroup
              value={conversationMode}
              onChange={(e) => setConversationMode(e.target.value)}
            >
              {CONVERSATION_MODES.map((mode, idx) => (
                <FormControlLabel
                  key={mode.value}
                  value={mode.value}
                  control={<Radio />}
                  sx={{
                    alignItems: "flex-start",
                    mb: idx === CONVERSATION_MODES.length - 1 ? 0 : 1,
                  }}
                  label={
                    <Box sx={{ pt: 0.75 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {mode.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mode.detail}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      {errors.length > 0 && (
        <Grid size={{ sx: 12 }}>
          <Typography
            variant={TYPOGRAPHY_VARIANT.H6}
            color={STATUS.WARNING}
            className="font-bold"
          >
            {DATA_UPLOAD_STRINGS.ERROR_FOUND}
          </Typography>
          <Box component="ul" className="list-disc list-inside" sx={{ px: 3 }}>
            {errors.map((error, idx) => (
              <Box component="li" key={idx} sx={{ pl: 1, pb: 0.5 }}>
                {error}
              </Box>
            ))}
          </Box>
        </Grid>
      )}
    </Container>
  );
};

export default DataUpload;